import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TenanciesSection from 'components/TenanciesSection/TenanciesSection';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import useTenancies from 'features/tenancies';
import useMapBoxSearch from 'features/mapbox-search';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ControlsSection from 'components/ControlsSection/ControlsSection';

const rangeDiffers = (current, defaults) =>
	Number(current[0]) !== defaults[0] || Number(current[1]) !== defaults[1];

const Tenancies = () => {

	const { t } = useTranslation();

	const sortOptions = [
		{ value: 'price', label: t('tenancies.sortOptions.cheapest') },
		{ value: 'newest', label: t('tenancies.sortOptions.newest') },
		{ value: 'available', label: t('tenancies.sortOptions.earliestAvailable') }
	];
	const defaultFilterValues = useMemo(() => {
		return {
			rent: [0, 30000],
			size: [0, 200],
			rooms: [0, 5],
			acquisition: null
		};
	}, []);

	const [searchQuery, setSearchQuery] = useState('');
	const [selectedPlace, setSelectedPlace] = useState(null);
	const [searchedData, setSearchedData] = useState(null);
	const [sortedData, setSortedData] = useState(null);
	const [selectedSortOption, setSelectedSortOption] = useState(sortOptions[0]);
	const [floorplanImageMode, setFloorplanImageMode] = useState(false);
	const [gridViewMode, setGridViewMode] = useState(true);
	const [rentFilterValue, setRentFilterValue] = useState(defaultFilterValues.rent);
	const [sizeFilterValue, setSizeFilterValue] = useState(defaultFilterValues.size);
	const [roomsFilterValue, setRoomsFilterValue] = useState(defaultFilterValues.rooms);
	const [acquisitionFilterValue, setAcquisitionFilterValue] = useState(null);
	const [balconyFilterValue, setBalconyFilterValue] = useState(false);
	const [petsFilterValue, setPetsFilterValue] = useState(false);
	const [elevatorFilterValue, setElevatorFilterValue] = useState(false);
	const [propertyFilterValue, setPropertyFilterValue] = useState(null);
	const [loading, setLoading] = useState(true);
	const { search } = useLocation();
	let history = useHistory();
	// Memoized on the search string: queryString.parse returns a new object
	// every call, and updateUrl/effects depend on it. An unstable identity
	// re-triggers the effect below on every render, causing an infinite
	// history.push loop (Safari kills the page with a SecurityError).
	const queryParamValues = useMemo(() => queryString.parse(search), [search]);

	const {
		isTenanciesLoading,
		tenanciesData,
		loadTenancies
	} = useTenancies();
	const {
		queryData,
		getPlaces
	} = useMapBoxSearch();	
	
	const checkIfInBbox = (point, bbox) => {
		// check for null values
		if (point === null || point?.longitude === null || point?.latitude === null) return false;
		if( bbox[0] <= point.longitude && point.longitude <= bbox[2] && bbox[1] <= point.latitude && point.latitude <= bbox[3] ) {
			return true;
		} else {
			return false;
		}
	};

	const matchesSelectedPostcode = (tenancy, place) => {
		if (!place?.place_type?.includes('postcode')) return false;
		const zipCode = tenancy.address?.zipCode;
		if (!zipCode) return false;
		const postcode = place.text_da || place.text;
		return String(zipCode) === String(postcode);
	};
	
	useEffect(() => {
		loadTenancies();
		if (queryParamValues.q) {
			getPlaces(queryParamValues.q);
		}
		if (queryParamValues.propertyId) {
			setPropertyFilterValue(queryParamValues.propertyId);
		}
		queryParamValues.rentFrom != null && queryParamValues.rentTo != null && setRentFilterValue([Number(queryParamValues.rentFrom), Number(queryParamValues.rentTo)]);
		queryParamValues.sizeFrom != null && queryParamValues.sizeTo != null && setSizeFilterValue([Number(queryParamValues.sizeFrom), Number(queryParamValues.sizeTo)]);
		queryParamValues.roomsFrom != null && queryParamValues.roomsTo != null && setRoomsFilterValue([Number(queryParamValues.roomsFrom), Number(queryParamValues.roomsTo)]);
		queryParamValues.balcony && setBalconyFilterValue(true);
		queryParamValues.pets && setPetsFilterValue(true);
		queryParamValues.elevator && setElevatorFilterValue(true);
		queryParamValues.acquisition && setAcquisitionFilterValue(new Date(queryParamValues.acquisition));	
	}, [
		getPlaces,
		loadTenancies,
		queryParamValues.q,
		queryParamValues.propertyId,
		queryParamValues.rentFrom,
		queryParamValues.rentTo,
		queryParamValues.sizeFrom,
		queryParamValues.sizeTo,
		queryParamValues.roomsFrom,
		queryParamValues.roomsTo,
		queryParamValues.balcony,
		queryParamValues.pets,
		queryParamValues.elevator,
		queryParamValues.acquisition,
	]);

	useEffect(() => {
		if (queryData !== null) {
			setSelectedPlace(queryData[0]);
			setSearchQuery(queryData[0].text_da);
		}
	}, [queryData]);
	useEffect(() => {
		if (!isTenanciesLoading && selectedPlace !== null) {
			const searched = tenanciesData?.items.filter(t => {
				if (selectedPlace.bbox) {
					return checkIfInBbox(t.address?.coordinates, selectedPlace.bbox) || matchesSelectedPostcode(t, selectedPlace);
				}
				else {
					const streetnameValid = t.address.street.includes(selectedPlace.text_da);
					const postnumberValid = t.address.zipCode.includes(selectedPlace.context[0].text_da);
					return streetnameValid && postnumberValid;
				}
				
			});
			setSearchedData(searched);
		} else if (!isTenanciesLoading && !queryParamValues.q) {
			setSearchedData(tenanciesData.items);
		}
	}, [isTenanciesLoading, selectedPlace, queryParamValues.q, tenanciesData]);

	const updateUrl = useCallback((pathname) => {
		// SearchBox may pass a path that already includes ?q=...
		let targetPathname = pathname;
		let q = queryParamValues.q;
		if (pathname && pathname.includes('?')) {
			const [path, query] = pathname.split('?');
			targetPathname = path;
			const pathParams = queryString.parse(query);
			if (pathParams.q) {
				q = pathParams.q;
			}
		}

		const params = {};
		if (q) {
			params.q = q;
		}
		if (queryParamValues.propertyId) {
			params.propertyId = queryParamValues.propertyId;
		}
		if (rangeDiffers(rentFilterValue, defaultFilterValues.rent)) {
			params.rentFrom = rentFilterValue[0];
			params.rentTo = rentFilterValue[1];
		}
		if (rangeDiffers(sizeFilterValue, defaultFilterValues.size)) {
			params.sizeFrom = sizeFilterValue[0];
			params.sizeTo = sizeFilterValue[1];
		}
		if (rangeDiffers(roomsFilterValue, defaultFilterValues.rooms)) {
			params.roomsFrom = roomsFilterValue[0];
			params.roomsTo = roomsFilterValue[1];
		}
		if (balconyFilterValue) {
			params.balcony = true;
		}
		if (petsFilterValue) {
			params.pets = true;
		}
		if (elevatorFilterValue) {
			params.elevator = true;
		}
		if (acquisitionFilterValue) {
			params.acquisition = acquisitionFilterValue.toLocaleDateString('da-DK');
		}

		const s = queryString.stringify(params);
		if (targetPathname) {
			history.push({
				pathname: targetPathname,
				search: s
			});
		} else {
			history.push({
				search: s
			});
		}
	}, [
		queryParamValues,
		rentFilterValue,
		sizeFilterValue,
		roomsFilterValue,
		balconyFilterValue,
		petsFilterValue,
		elevatorFilterValue,
		acquisitionFilterValue,
		defaultFilterValues,
		history,
	]);

	useEffect(() => {
		const sortData = (data) => {
			if (data !== null) {
				let sorted;
				switch(selectedSortOption.value) {
				case 'price':
					sorted = data.sort((a, b) => {
						return a.monthlyRent.value - b.monthlyRent.value;
					});
					break;
				case 'size':
					sorted = data.sort((a, b) => {
						return a.size.value - b.size.value;
					});
					break;
				case 'newest':
					sorted = data.sort((a, b) => {
						const d1 = new Date(a.publishedAt);
						const d2 = new Date(b.publishedAt);
						return d2.getTime() - d1.getTime();
					});
					break;
				case 'available':
					sorted = data.sort((a, b) => {
						const d1 = new Date(a.availableFrom);
						const d2 = new Date(b.availableFrom);
						return d1.getTime() - d2.getTime();
					});
					break;
				}
				sorted = sorted.sort((a, b) => (a.classification === 'Other') - (b.classification === 'Other'));
				return sorted;
			}
		};
		const filterData = (data) => {
			if (data !== null) {
				let filtered;
				filtered = data.filter(item => {
					if (rentFilterValue[1] === defaultFilterValues.rent[1]) {
						return item.monthlyRent.value >= rentFilterValue[0];
					}
					return (item.monthlyRent.value >= rentFilterValue[0] && item.monthlyRent.value <= rentFilterValue[1]);
				});
				filtered = filtered.filter(item => {
					if (sizeFilterValue[1] === defaultFilterValues.size[1]) {
						return item.size.value >= sizeFilterValue[0];
					}
					return (item.size.value >= sizeFilterValue[0] && item.size.value <= sizeFilterValue[1]);
				});
				filtered = filtered.filter(item => {
					if (roomsFilterValue[1] === defaultFilterValues.rooms[1]) {
						return item.rooms >= roomsFilterValue[0];
					}
					return (item.rooms >= roomsFilterValue[0] && item.rooms <= roomsFilterValue[1]);
				});
				
				if (petsFilterValue === true) {
					filtered = filtered.filter(item => {
						return item.additionalDetails.petsAllowed === true;
					});
				}
				if (balconyFilterValue === true) {
					filtered = filtered.filter(item => {
						return item.tenancyFacilities.includes('Balcony');
					});
				}
				if (elevatorFilterValue === true) {
					filtered = filtered.filter(item => {
						return item.propertyFacilities.includes.includes('Elevator');
					});
				}
				
				if (acquisitionFilterValue !== null) {
					filtered = filtered.filter(item => {
						const ta = new Date(item.availableFrom);
						const fa = new Date(acquisitionFilterValue);
						return ta.getTime() < fa.getTime();
					});
				}
				if (propertyFilterValue !== null) {
					filtered = filtered.filter(item => {
						return item.propertyId === propertyFilterValue;
					});
				}
				return filtered;
			}
		};
		if (searchedData !== null) {
			const temp = sortData(searchedData);
			const filteredData = filterData([...temp]);
			setSortedData([...filteredData]);
			setLoading(false);
		}
		updateUrl();
	}, [selectedSortOption, searchedData, rentFilterValue, sizeFilterValue, roomsFilterValue, balconyFilterValue, petsFilterValue, elevatorFilterValue, acquisitionFilterValue, defaultFilterValues, propertyFilterValue, updateUrl]);

	const updateSelectedPlace = (data, type) => {
		if (type === 'string') {
			getPlaces(data);
		} else if (type === 'place') {
			setSelectedPlace(data);
			setSearchQuery(data.text_da);
		}
	};	

	return (
		<div>
			<ControlsSection 
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
				updateSelectedPlace={updateSelectedPlace}
				sortOptions={sortOptions}
				selectedSortOption={selectedSortOption}
				setSelectedSortOption={setSelectedSortOption}
				gridViewMode={gridViewMode}
				setGridViewMode={setGridViewMode}
				floorplanImageMode={floorplanImageMode}
				setFloorplanImageMode={setFloorplanImageMode}
				rentFilterValue={rentFilterValue}
				setRentFilterValue={setRentFilterValue}
				sizeFilterValue={sizeFilterValue}
				setSizeFilterValue={setSizeFilterValue}
				roomsFilterValue={roomsFilterValue}
				setRoomsFilterValue={setRoomsFilterValue}
				defaultFilterValues={defaultFilterValues}
				balconyFilterValue={balconyFilterValue}
				setBalconyFilterValue={setBalconyFilterValue}
				petsFilterValue={petsFilterValue}
				setPetsFilterValue={setPetsFilterValue}
				elevatorFilterValue={elevatorFilterValue}
				setElevatorFilterValue={setElevatorFilterValue}
				updateUrl={updateUrl}
				acquisitionFilterValue={acquisitionFilterValue}
				setAcquisitionFilterValue={setAcquisitionFilterValue}

			/>
			<TenanciesSection
				loading={loading} 
				filteredTenancies={sortedData}
				allTenancies={tenanciesData?.items}
				floorplanImageMode={floorplanImageMode}
				gridViewMode={gridViewMode}
				selectedPlace={selectedPlace}
			/>
		</div>
	);
};

export default Tenancies;